import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Vehicle data (replace with real API data as needed)
const expiredVehicles = [
  { number: "MH12AB1234", renewal: "2024-05-10" },
  { number: "DL09CD5678", renewal: "2024-04-22" },
  { number: "KA03EF9012", renewal: "2024-03-15" },
  { number: "GJ01GH3456", renewal: "2024-02-28" },
  { number: "TN10IJ7890", renewal: "2024-01-30" },
  { number: "RJ14KL2345", renewal: "2023-12-15" },
  { number: "UP32MN6789", renewal: "2023-11-20" },
  { number: "WB20OP1234", renewal: "2023-10-05" },
];

const plans = [
  {
    name: "Basic",
    price: "₹299",
    validity: "1 Month",
    value: "1m",
    description: "Best for short-term use",
    color: "#1565C0",
    bgColor: "#E3F2FD",
  },
  {
    name: "Pro",
    price: "₹1499",
    validity: "6 Months",
    value: "6m",
    description: "Great value for regular users",
    color: "#2E7D32",
    bgColor: "#E8F5E9",
  },
  {
    name: "Premium",
    price: "₹2499",
    validity: "1 Year",
    value: "1y",
    description: "Maximum savings for long-term",
    color: "#FF9800",
    bgColor: "#FFF3E0",
  },
];

interface Vehicle {
  number: string;
  renewal: string;
}

const ExpiredVehicles: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(plans[0].value);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const handleViewClick = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVehicle(null);
  };

  const handlePayment = () => {
    // Add payment logic here
    console.log(
      "Processing payment for:",
      selectedVehicle?.number,
      "Plan:",
      selectedPlan
    );
    handleCloseModal();
    // You can add actual payment integration here
  };

  const getSelectedPlan = () => {
    return plans.find((plan) => plan.value === selectedPlan) || plans[0];
  };

  const formatRenewalDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isExpiredSoon = (dateString: string) => {
    const renewalDate = new Date(dateString);
    const today = new Date();
    const diffTime = renewalDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30; // Expired or expiring within 30 days
  };

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 4,
            bgcolor: "#f8fafc",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <ScheduleIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="bold">
            Expired Vehicles
          </Typography>
        </Paper>

        {/* Vehicles Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: "#e2e8f0",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#e3f2fd" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", color: "#1565C0" }}>
                    Vehicle Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#1565C0" }}>
                    Renewal Date
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", color: "#1565C0" }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiredVehicles.map((vehicle, index) => (
                  <TableRow
                    key={vehicle.number}
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: "#f9f9f9",
                      },
                      "&:hover": {
                        bgcolor: "#f0f8ff",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1" fontWeight="600">
                        {vehicle.number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatRenewalDate(vehicle.renewal)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleViewClick(vehicle)}
                        sx={{
                          color: "#1565C0",
                          "&:hover": {
                            bgcolor: alpha("#1565C0", 0.1),
                          },
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Renewal Plans Modal */}
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              minHeight: "60vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#1565C0",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 2,
              py: 3,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
              Renew Now
            </Typography>
            <IconButton onClick={handleCloseModal} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, color: "#333" }}>
              Vehicle: <strong>{selectedVehicle?.number}</strong>
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, color: "#666" }}>
              Select Plan:
            </Typography>

            <RadioGroup
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              sx={{ gap: 2 }}
            >
              {plans.map((plan) => {
                const isSelected = selectedPlan === plan.value;
                return (
                  <Card
                    key={plan.value}
                    sx={{
                      border: `2px solid ${
                        isSelected ? plan.color : "#e0e0e0"
                      }`,
                      borderRadius: 3,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      bgcolor: isSelected ? plan.bgColor : "#fff",
                      "&:hover": {
                        borderColor: plan.color,
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 25px ${alpha(plan.color, 0.2)}`,
                      },
                    }}
                    onClick={() => setSelectedPlan(plan.value)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <FormControlLabel
                        value={plan.value}
                        control={
                          <Radio
                            sx={{
                              color: plan.color,
                              "&.Mui-checked": {
                                color: plan.color,
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ flex: 1, ml: 1 }}>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{
                                color: isSelected ? plan.color : "#333",
                                mb: 1,
                              }}
                            >
                              {plan.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                  color: isSelected ? plan.color : "#333",
                                  mr: 1,
                                }}
                              >
                                {plan.price}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {plan.validity}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {plan.description}
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, width: "100%" }}
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </RadioGroup>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 4 }}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{ mr: 2, borderRadius: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              variant="contained"
              startIcon={<PaymentIcon />}
              sx={{
                bgcolor: getSelectedPlan().color,
                borderRadius: 3,
                px: 2,
                py: 1,
                fontSize: "0.9rem",
                fontWeight: "semibold",
                "&:hover": {
                  bgcolor: getSelectedPlan().color,
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 25px ${alpha(
                    getSelectedPlan().color,
                    0.3
                  )}`,
                },
              }}
            >
              PAY NOW
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ExpiredVehicles;
